import React, { useMemo, useEffect, useCallback } from "react";
import styled from "styled-components";

import { MapAnalytics } from "@components/Map";
import { MarkerOptions } from "@shared/interfaces/map.interface";
import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalysisMetrics, TravelAnalysisMapMarker } from "../../types";
import { ChartContainer } from "../../styles";

/** Custom event name for InfoWindow alert type clicks. */
const ALERT_TYPE_CLICK_EVENT = "travel-analysis:alert-type-click";

/** Wrapper that overrides the default Google Maps InfoWindow styling. */
const MapWrapper = styled.div`
  .gm-style-iw-c {
    padding: 14px !important;
  }

  /* Remove the header container and close button entirely */
  .gm-style-iw-ch,
  .gm-ui-hover-effect {
    display: none !important;
  }
`;

/** Props for the GeographicMonitoring organism. */
interface GeographicMonitoringProps {
  metrics: TravelAnalysisMetrics | null;
  loading: boolean;
  mapMarkers: TravelAnalysisMapMarker[];
  markersLoading: boolean;
  onAlertTypeClick?: (alertType: string, originName: string) => void;
}

/**
 * Renders a Google Maps section with alert markers and summary counters.
 * Each marker represents a travel origin with its alert count and types shown in an InfoWindow.
 */
const GeographicMonitoring: React.FC<GeographicMonitoringProps> = ({ metrics, loading, mapMarkers, markersLoading, onAlertTypeClick }) => {

  const { t } = useTranslation();

  // Listen for custom events dispatched from InfoWindow onclick handlers
  useEffect(() => {
    if (!onAlertTypeClick) return;

    const handler = (e: Event) => {
      const { alertType, originName } = (e as CustomEvent).detail;

      onAlertTypeClick(alertType, originName);
    };

    window.addEventListener(ALERT_TYPE_CLICK_EVENT, handler);

    return () => window.removeEventListener(ALERT_TYPE_CLICK_EVENT, handler);
  }, [onAlertTypeClick]);

  /** Convert API map markers to MapAnalytics MarkerOptions with label and InfoWindow. */
  const markerOptions: MarkerOptions[] = useMemo(() => {

    if (!mapMarkers || mapMarkers.length === 0) return [];

    return mapMarkers.map((m) => {
      const lat = parseFloat(m.latitude);
      const lng = parseFloat(m.longitude);

      if (isNaN(lat) || isNaN(lng)) return null;

      const tdStyle = "font-size:12px;padding:2px 8px 2px 0;color:#1a73e8;cursor:pointer;";
      const tdCountStyle =
        "font-size:12px;font-weight:600;text-align:right;color:#1a73e8;cursor:pointer;";
      const originLabel = m.originName || `${m.latitude}, ${m.longitude}`;
      const escapedOrigin = originLabel.replace(/'/g, "\\'");
      const alertRows = m.alertsByType
        .map((a) => {
          const escapedType = a.type.replace(/'/g, "\\'");
          // eslint-disable-next-line max-len
          const onclick = `window.dispatchEvent(new CustomEvent('${ALERT_TYPE_CLICK_EVENT}',{detail:{alertType:'${escapedType}',originName:'${escapedOrigin}'}}))`;

          // eslint-disable-next-line max-len
          return `<tr onclick="${onclick}" style="cursor:pointer;" title="Clique para filtrar"><td style="${tdStyle}">${a.type}</td><td style="${tdCountStyle}">${a.count}</td></tr>`;
        })
        .join("");

      const infoContent = `
        <div style="min-width:200px;font-family:Roboto,Arial,sans-serif;">
          <strong style="font-size:14px;">${m.originName || `${m.latitude}, ${m.longitude}`}</strong><br/>
          <span style="font-size:12px;color:#666;">
            ${m.travelCount} viagen${m.travelCount > 1 ? "s" : ""}
            · ${m.totalAlerts} alerta${m.totalAlerts > 1 ? "s" : ""}
          </span>
          <hr style="margin:8px 0;border:none;border-top:1px solid #e0e0e0;"/>
          <b style="font-size:13px;">Alertas por tipo:</b>
          <table style="width:100%;border-collapse:collapse;margin-top:4px;">
            ${alertRows}
          </table>
        </div>
      `;

      return {
        position: { lat, lng },
        infoContent,
        class: "alert_marker",
        label: {
          text: String(m.totalAlerts),
          color: "#fff",
          fontSize: "11px",
          fontWeight: "bold"
        }
      } as MarkerOptions;
    }).filter(Boolean) as MarkerOptions[];
  }, [mapMarkers]);

  /** Filter config that keeps our markers visible on the map. */
  const mapFilter = useMemo(() => [{ class: "alert_marker", status: true }], []);

  if (!loading && !markersLoading && !metrics) {
    return (
      <ChartContainer>
        <div className="chart-title">{t(TravelAnalysisMessages.geographicMonitoringTitle)}</div>
        <div className="chart-empty">{t(TravelAnalysisMessages.noGeographicData)}</div>
      </ChartContainer>
    );
  }

  const alertsCount = metrics ? metrics.totalAnalyzed - metrics.successCount : 0;

  return (
    <ChartContainer>
      <div className="chart-title">{t(TravelAnalysisMessages.geographicMonitoringTitle)}</div>
      <p style={{ fontSize: "0.8rem", color: "#999", marginBottom: 12 }}>
        {t(TravelAnalysisMessages.geographicMonitoringSubtitle)}
      </p>
      <MapWrapper>
        <MapAnalytics key={`travel-map-${markerOptions.length}`} mapHeight={400} markers={markerOptions} filter={mapFilter} />
      </MapWrapper>
      <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
        <div style={{ fontSize: "0.875rem", color: "#333" }}>
          <strong>{t(TravelAnalysisMessages.activeVehicles)}:</strong>{" "}
          {metrics ? metrics.totalAnalyzed.toLocaleString("pt-BR") : "—"}
        </div>
        <div style={{ fontSize: "0.875rem", color: "#333" }}>
          <strong>{t(TravelAnalysisMessages.activeAlerts)}:</strong>{" "}
          {metrics ? alertsCount.toLocaleString("pt-BR") : "—"}
        </div>
      </div>
    </ChartContainer>
  );
};

export default GeographicMonitoring;
